/**
 * Firebase initialisation.
 *
 * Config values come from environment variables (Vite reads .env files
 * with VITE_ prefix). The user fills these in once at frontend-pro/.env
 * — see frontend-pro/README.md for the full list.
 *
 * NOTE: this app reuses the existing stocksense-ai-491717 Firebase
 * project (per CLAUDE.md: "Firebase Auth project — stocksense-ai-491717
 * reused from original project"). Don't create a new project; the
 * existing auth users carry over.
 *
 * App Check is mandatory — the Cloud Run backend enforces it via the
 * `X-Firebase-AppCheck` header. Without an initialized App Check
 * instance every API call would 401 with "App Check verification
 * failed". Same reCAPTCHA Enterprise site key as the apex SPA.
 */
import { initializeApp, type FirebaseOptions } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken as getAppCheckToken,
  type AppCheck,
} from "firebase/app-check";

const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);

// Single Google provider instance — reused across sign-in / signup
// entry points so prompt-on-every-click behavior is consistent.
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

// reCAPTCHA Enterprise site key — shared with the apex SPA so the same
// project-level App Check enforcement covers both apps. Override via
// VITE_RECAPTCHA_SITE_KEY in .env if you ever rotate the key.
const RECAPTCHA_SITE_KEY =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined) ||
  "6Ldaz-IsAAAAAK0eBTQSsMagjAbt6h2XNWS_6h8S";

let appCheck: AppCheck | null = null;
try {
  appCheck = initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
} catch (e) {
  // initializeAppCheck throws if called twice (HMR in dev) — swallow
  // and reuse. In prod this branch never fires.
  console.warn("[firebase] App Check init skipped:", e);
}

/** Resolve the current App Check token. Returns `null` when the SDK
 *  can't mint one (e.g. reCAPTCHA blocked). The api.ts client attaches
 *  it to every request as `X-Firebase-AppCheck`. */
export async function appCheckHeader(): Promise<Record<string, string>> {
  if (!appCheck) return {};
  try {
    const t = await getAppCheckToken(appCheck);
    return { "X-Firebase-AppCheck": t.token };
  } catch (e) {
    console.warn("[firebase] App Check token fetch failed:", e);
    return {};
  }
}
